import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { login } from '../api/auth';
import { useAuthStore } from '../store/auth';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

export default function LoginPage() {
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const toggleLang = () => {
    const next = i18n.language === 'ka' ? 'en' : 'ka';
    i18n.changeLanguage(next);
    localStorage.setItem('liber_lang', next);
  };


  React.useEffect(() => {
    const canvas = document.getElementById('bgCanvas') as HTMLCanvasElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    type Book = {x:number,y:number,w:number,h:number,angle:number,va:number,vy:number,vx:number,color:string,opacity:number};
    const colors = ['#1d4ed8','#1e3a6e','#0f2044','#2a3f6e','#334155','#1a2744'];
    const books: Book[] = [];

    for (let i = 0; i < 25; i++) {
      books.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        w: Math.random() * 30 + 16,
        h: Math.random() * 44 + 28,
        angle: Math.random() * Math.PI * 2,
        va: (Math.random() - 0.5) * 0.008,
        vy: (Math.random() - 0.5) * 0.4,
        vx: (Math.random() - 0.5) * 0.3,
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: Math.random() * 0.5 + 0.15
      });
    }

    let animId: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      books.forEach(b => {
        ctx.save();
        ctx.translate(b.x, b.y);
        ctx.rotate(b.angle);
        ctx.globalAlpha = b.opacity;
        ctx.fillStyle = b.color;
        ctx.beginPath();
        ctx.roundRect(-b.w/2, -b.h/2, b.w, b.h, 3);
        ctx.fill();
        ctx.strokeStyle = '#60a5fa';
        ctx.lineWidth = 0.5;
        ctx.stroke();
        ctx.fillStyle = '#60a5fa';
        ctx.fillRect(-b.w/2 + 3, -b.h/2 + 4, 2, b.h - 8);
        ctx.restore();
        b.x += b.vx; b.y += b.vy; b.angle += b.va;
        if (b.x < -60) b.x = canvas.width + 60;
        if (b.x > canvas.width + 60) b.x = -60;
        if (b.y < -60) b.y = canvas.height + 60;
        if (b.y > canvas.height + 60) b.y = -60;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await login(form);
      setAuth(data.token, data.user);
      navigate('/');
    } catch {
      toast.error(t('invalid_credentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{minHeight:'100vh',background:'#080c14',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'system-ui,sans-serif',position:'relative',overflow:'hidden'}}>
      <canvas id="bgCanvas" style={{position:'absolute',inset:0,width:'100%',height:'100%',opacity:0.35,pointerEvents:'none'}} />
      <div style={{width:'100%',maxWidth:380}}>
        <div style={{background:'#0d1424',border:'1px solid #1e2d45',borderRadius:16,padding:32}}>
          
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:32}}>
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <img src="/logo.svg" alt="Folios" style={{width:88,height:88,borderRadius:20}} />
              <div>
                <div style={{fontSize:18,fontWeight:600,color:'#f1f5f9'}}>Folios</div>
                <div style={{fontSize:11,color:'#475569'}}>Library Management System</div>
              </div>
            </div>
            <button onClick={toggleLang}
              style={{display:'flex',alignItems:'center',gap:5,background:'#111827',border:'1px solid #1e2d45',borderRadius:8,padding:'5px 10px',cursor:'pointer',color:'#64748b',fontSize:12}}>
              <Globe size={13} />
              {i18n.language === 'ka' ? 'EN' : 'KA'}
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{marginBottom:16}}>
              <label style={{display:'block',fontSize:12,color:'#64748b',marginBottom:6}}>{t('username')}</label>
              <input type="text" required value={form.username}
                onChange={e => setForm({...form, username: e.target.value})}
                style={{width:'100%',background:'#080c14',border:'1px solid #1e2d45',borderRadius:8,padding:'10px 12px',fontSize:13,color:'#e2e8f0',outline:'none',boxSizing:'border-box'}} />
            </div>
            <div style={{marginBottom:24}}>
              <label style={{display:'block',fontSize:12,color:'#64748b',marginBottom:6}}>{t('password')}</label>
              <input type="password" required value={form.password}
                onChange={e => setForm({...form, password: e.target.value})}
                style={{width:'100%',background:'#080c14',border:'1px solid #1e2d45',borderRadius:8,padding:'10px 12px',fontSize:13,color:'#e2e8f0',outline:'none',boxSizing:'border-box'}} />
            </div>
            <button type="submit" disabled={loading}
              style={{width:'100%',background:'#1d4ed8',border:'none',borderRadius:8,padding:'11px',fontSize:13,fontWeight:500,color:'white',cursor:'pointer',opacity:loading?0.6:1}}>
              {loading ? t('signing_in') : t('sign_in')}
            </button>
          </form>

          <div style={{marginTop:16,textAlign:'center'}}>
            <a href='/opac' style={{fontSize:12,color:'#60a5fa',textDecoration:'none'}}>📚 ონლაინ კატალოგი (OPAC)</a>
          </div>
          <div style={{marginTop:16,paddingTop:16,borderTop:'1px solid #1e2d45',textAlign:'center'}}>
            <p style={{color:'#1e3a5f',fontSize:11}}>{t('copyright')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
